
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {FaUserCircle,FaGlobe,FaFileAlt,FaExternalLinkAlt,FaTimes,FaImage,FaFilePdf,FaFilePowerpoint,FaDownload,} from "react-icons/fa";
import { feedItems, FeedItem } from "../../../components/DemoShowcase";

const ShowcasePage: React.FC = () => {
const navigate = useNavigate();

const [activeTag, setActiveTag] = useState("ALL");
const [modalItem, setModalItem] = useState<FeedItem | null>(null);

const tags = [
    "ALL",
    "PROJECT",
    "ENTERTAINMENT",
    "RANDOM",
    "IMAGE",
];

const getVideoUrl = (item: FeedItem): string => {
    if (item.type === "video") {
    return item.videoUrl;
    }

    if (item.type === "project" && item.videoUrl) {
    return item.videoUrl;
    }

    return "";
};

const hasVideo = (item: FeedItem): boolean => {
    return !!getVideoUrl(item);
};

const getImageUrl = (item: FeedItem): string => {
    if (item.type === "image") {
    return item.imageUrl;
    }

    if (item.type === "project") {
    return item.imageUrl || item.projectData.banner_image || "";
    }

    return "";
};

const getYoutubeThumbnail = (url: string) => {
    const id =
    url.match(/embed\/([^?]+)/)?.[1] ||
    url.match(/v=([^&]+)/)?.[1] ||
    url.match(/youtu\.be\/([^?]+)/)?.[1];

    return id
    ? `https://img.youtube.com/vi/${id}/hqdefault.jpg`
    : "";
};

const getDocumentViewerUrl = (
    item: Extract<FeedItem, { type: "document" }>
) => {
    const fileUrl = new URL(
    item.fileUrl,
    window.location.origin
    ).href;

    if (item.fileType === "pdf") {
    return fileUrl;
    }

    return (
    "https://view.officeapps.live.com/op/embed.aspx?src=" +
    encodeURIComponent(fileUrl)
    );
};

const filteredItems = useMemo(() => {
    let result = [...feedItems];

    if (activeTag !== "ALL") {
    if (activeTag === "IMAGE") {
        result = result.filter(
        (item) =>
            item.type === "image" ||
            (item.type === "project" &&
            !hasVideo(item) &&
            !!getImageUrl(item))
        );
    } else {
        result = result.filter((item) => {
        const category = (item.category || "").toUpperCase();
        const descLower = (item.content || "").toLowerCase();

        return (
            category === activeTag ||
            descLower.includes(activeTag.toLowerCase())
        );
        });
    }
    }

    return result.sort((a, b) => b.id - a.id);
}, [activeTag]);

return (
    <div className="starry-bg min-h-screen w-full bg-[#020617] text-slate-200 relative">
        <div className="sticky top-0 z-40 border-b border-slate-800 bg-[#020617]/90 backdrop-blur">
            <div className="px-3 py-3 flex gap-2 overflow-x-auto no-scrollbar">
            {tags.map((tag) => (
                <button
                key={tag}
                onClick={() => setActiveTag(tag)}
                className={`text-[10px] px-3 py-1 uppercase tracking-widest border transition ${
                    activeTag === tag
                    ? "border-purple-500 text-purple-400"
                    : "border-slate-800 text-slate-500"
                }`}
                >
                {tag}
                </button>
            ))}
            </div>
        </div>

    <div className="w-full px-2 sm:px-4 py-3 mb-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
        {filteredItems.length === 0 ? (
            <div className="col-span-full text-center text-slate-500 py-10">
            Tidak ada konten untuk kategori "{activeTag}".
            </div>
        ) : (
            filteredItems.map((item) => {
            const isVid = hasVideo(item);
            const videoUrl = getVideoUrl(item);
            const imgUrl = getImageUrl(item);
            const isDoc = item.type === "document";

            const isPlayable =
                item.type === "project" &&
                item.projectData.playable &&
                !!item.projectData.path;

            const isText =
                !isVid && !imgUrl && !isPlayable && !isDoc;

            const isDim =
                modalItem !== null && modalItem.id !== item.id;

            return (
                <div
                key={item.id}
                className={`
                    bg-[#0f172a]/40 border border-slate-800
                    transition-all duration-300 cursor-pointer group
                    ${isDim ? "opacity-20 scale-[0.98]" : ""}
                    hover:border-purple-500/50
                    hover:shadow-[0_0_20px_rgba(168,85,247,0.15)]
                `}
                onClick={() => {
                    if (isPlayable && item.type === "project") {
                    const path = item.projectData.path!;

                    if (
                        path.startsWith("http://") ||
                        path.startsWith("https://")
                    ) {
                        window.open(
                        path,
                        "_blank",
                        "noopener,noreferrer"
                        );
                    } else {
                        navigate(path);
                    }

                    return;
                    }

                    setModalItem(item);
                }}
                >
                <div className="aspect-video bg-black relative overflow-hidden">
                    {isVid && videoUrl ? (
                    <>
                        <img
                        src={
                            getYoutubeThumbnail(videoUrl) ||
                            "https://placehold.co/640x360?text=Video+Preview"
                        }
                        className="w-full h-full object-cover"
                        alt={item.content || "Video"}
                        onError={(e) => {
                            e.currentTarget.src =
                            "https://placehold.co/640x360?text=No+Preview";
                        }}
                        />

                        <div className="absolute inset-0 bg-black/30" />

                        <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-10 h-10 bg-black/60 border border-white/10 rounded-full flex items-center justify-center text-xs text-white">
                            ▶
                        </div>
                        </div>
                    </>
                    ) : isDoc && item.type === "document" ? (
                    <img
                        src={item.thumbnailUrl || "/K_1.png"}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        alt={item.content || "Document"}
                        onError={(e) => {
                        e.currentTarget.src = "/K.png";
                        }}
                    />
                    ) : imgUrl ? (
                    <img
                        src={imgUrl}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        alt={item.content || "Image"}
                        onError={(e) => {
                        e.currentTarget.src =
                            "https://placehold.co/640x360?text=No+Image";
                        }}
                    />
                    ) : item.type === "project" ? (
                    <div className="flex items-center justify-center h-full text-2xl text-slate-400">
                        {item.projectData.icon || "🚀"}
                    </div>
                    ) : (
                    <div className="flex items-center justify-center h-full text-[10px] text-slate-600">
                        TEXT
                    </div>
                    )}

                    {/* BADGES */}
                    <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/70 backdrop-blur-sm px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border border-white/10">
                    {isPlayable && (
                        <>
                        <FaGlobe size={8} className="text-cyan-400" />
                        <span className="text-purple-300">
                            Playable
                        </span>
                        </>
                    )}

                    {isDoc && item.type === "document" && (
                        <>
                        {item.fileType === "pdf" ? (
                            <FaFilePdf
                            size={8}
                            className="text-red-400"
                            />
                        ) : (
                            <FaFilePowerpoint
                            size={8}
                            className="text-orange-400"
                            />
                        )}

                        <span className="text-purple-300">
                            {item.fileType.toUpperCase()}
                        </span>
                        </>
                    )}

                    {imgUrl && !isVid && (
                        <>
                        <FaImage
                            size={8}
                            className="text-emerald-400"
                        />
                        <span className="text-emerald-300">
                            Image
                        </span>
                        </>
                    )}

                    {isText && (
                        <>
                        <FaFileAlt
                            size={8}
                            className="text-slate-400"
                        />
                        <span className="text-purple-300">
                            Text
                        </span>
                        </>
                    )}
                    </div>

                    {isPlayable && (
                    <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm rounded-full p-1.5 border border-white/10">
                        <FaExternalLinkAlt
                        size={10}
                        className="text-cyan-300"
                        />
                    </div>
                    )}
                </div>

                <div className="p-2 border-t border-slate-800 flex gap-2 bg-gray-900">
                    <FaUserCircle className="text-slate-600 text-sm flex-shrink-0 mt-0.5" />

                    <div className="min-w-0">
                    <div className="text-[10px] text-slate-300 truncate">
                        {item.content || "Untitled"}
                    </div>
                    </div>
                </div>
                </div>
            );
            })
        )}
        </div>
    </div>

    {modalItem && (
        <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-xl p-2 sm:p-4"
        onClick={() => setModalItem(null)}
        >
            <div
                className="relative w-full max-w-5xl bg-[#0a0a1a] border border-purple-500/40 shadow-[0_0_80px_rgba(168,85,247,0.25)] rounded-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={() => setModalItem(null)}
                    className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-black/70 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white hover:bg-purple-500/40 hover:border-purple-400 transition-all duration-300"
                    aria-label="Close modal"
                >
                <FaTimes size={14} />
                </button>

                {hasVideo(modalItem) ? (
                <div className="w-full aspect-video bg-black">
                    <iframe
                    className="w-full h-full"
                    src={`${getVideoUrl(modalItem)}${getVideoUrl(modalItem).includes("?") ? "&" : "?"}autoplay=1&rel=0`}
                    allowFullScreen
                    allow="autoplay; encrypted-media"
                    title="Showcase Video"
                    />
                    </div>
                ) : modalItem.type === "document" ? (
                    <div className="w-full bg-[#050510]">
                    <div className="px-4 py-3 pr-14 flex items-center justify-between gap-3 border-b border-purple-500/20">
                        <div className="flex items-center gap-2 min-w-0">
                            {modalItem.fileType === "pdf" ? (
                            <FaFilePdf
                                size={22}
                                className="text-red-400 flex-shrink-0"
                            />
                            ) : (
                            <FaFilePowerpoint
                                size={22}
                                className="text-orange-400 flex-shrink-0"
                            />
                            )}

                            <span className="text-white text-sm font-bold truncate">
                            {modalItem.content}
                            </span>
                        </div>
                    </div>

                    <iframe
                        key={`${modalItem.id}-${modalItem.fileUrl}`}
                        src={getDocumentViewerUrl(modalItem)}
                        title={modalItem.content}
                        className="w-full h-[75vh] min-h-[350px] bg-black border-0"
                        allowFullScreen
                    />

                    </div>
                ) : (
                <div className="w-full relative">
                    <div className="absolute top-0 left-0 right-0 z-10 bg-black/60 backdrop-blur-sm border-b border-purple-500/20 px-4 py-3 flex items-center pr-14">
                    <span className="text-white text-sm font-bold uppercase tracking-wider truncate">
                        {modalItem.content || "Detail Item"}
                    </span>
                    </div>

                    <div className="aspect-video w-full bg-black/50 flex items-center justify-center p-4 lg:p-6 pt-16">
                    <img
                        src={getImageUrl(modalItem) || "/K.png"}
                        className="w-full h-full object-contain"
                        alt={modalItem.content || "Detail"}
                        onError={(e) => {
                        e.currentTarget.src = "/K.png";
                        }}
                    />
                    </div>
                </div>
                )}
            </div>
        </div>
    )}
    </div>
);
};

export default ShowcasePage;
